import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { cloneDeep, isEqual } from 'lodash';
import { CircularProgress } from '@mui/material';
import { ApiCustomField, ApiEnumGroup, ApiItemTypes, type ApiAddress, type ApiImageWrapper, type ApiOrganization, type ApiOrgOverview } from '@agunity/api-v4';

import AgButton from '../../stories/Components/AgButton';
import AgTypography from '../../stories/Styles/AgTypography';
import AgProfilePage from '../../stories/Components/PageTemplates/AgProfilePage';
import defaultAvatar from '../../stories/Components/AgAvatar/defaultAvatar.svg';
import AgToast from '../../stories/Components/AgToast';
import AgModal from '../../stories/AgModal';
import AgAvatar from '../../stories/Components/AgAvatar';
import OrganizationDetails from './OrganizationDetails';
import Settings from './Settings';
import RightSidebar from './RightSidebar';
import Other from './Other';
import ContractForm from '../../components/ContractForm';
import type { AgTabsDataProps } from '../../stories/Components/AgTab';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getCustomFieldByTagAndFunction, getEnumList } from '../Users/redux';
import { getImage, getOrganization, getOrgSummary, getStructure, saveImage, saveOrg, selectIsOpenViewAvatar, setFunctionStructureList, setIsDirty_Org_Details, setIsOpenUnsavedModal, setIsOpenViewAvatar, setOrg_EnumList, setOrg_Redirect_URL, setOrg_TagList, setSelectedOrgName } from '../../redux/slices/organizations';
import { ADDRESS_MAX_LENGTH, ASSET_TYPES_READ_PERMISSION, ASSET_TYPES_WRITE_PERMISSION, CustomFieldItemType, MEMBER_EDIT_PERMISSION, MEMBER_FIELD_NAME_NOTES, MEMBER_PARENT_FIELD_TYPE_NUMBER, ORG_NOTES_MAX_LENGTH, ORG_TYPE_ID, POST_CODE_MAX_LENGTH, STANDARD_TEXT_MAX_LENGTH, SUCCESS_STATUS, TEMPLATE_STATUS } from '../../Constants';
import { changeStatus } from '../Search/redux';
import { getLicenseInfo } from '../../redux/slices/license';
import { IlicenseFlags } from './types';

// Wrapper to inject Org ID as props
export const OrgRouteElementWrapper = () => {
  const { orgId } = useParams();
  if (!orgId) return null;
  return <AgTypography sx={{ padding: '16px 32px', }}>
    <OrganizationInfo orgId={Number(orgId)} />
  </AgTypography>;
};

const OrganizationInfo = ({ orgId }: {orgId?: number}) => {
  const dispatch = useAppDispatch();
  const messages = useIntl().formatMessage;
  const navigate = useNavigate();

  const orgTree = useAppSelector(state => state.global.orgTree);
  const isRootOrg = Number(orgTree?.id) === Number(orgId);

  const functionStructureList = useAppSelector((state) => state.orgs.functionStructureList) || [];
  
  const user = useAppSelector(state => state.global.users);
  const permissions = (user?.length && user[0].permissions) || [];
  const editAccess = permissions.includes(MEMBER_EDIT_PERMISSION);
  const canReadAssetType = permissions.includes(ASSET_TYPES_READ_PERMISSION);
  const canWriteAssetType = permissions.includes(ASSET_TYPES_WRITE_PERMISSION);
  const canDoOther = [canWriteAssetType, canReadAssetType].some(Boolean);

  const isOpenViewAvatar = useAppSelector(selectIsOpenViewAvatar);
  const isOpenUnsavedModal = useAppSelector(state => state.orgs.isOpenUnsavedModal);
  const redirect_URL = useAppSelector(state => state.orgs.org_Redirect_URL);
  const hasTagList = useAppSelector(state => state.orgs.tagList);
  const hasEnumList = useAppSelector(state => state.orgs.enumList);

  const [orgDetails, setOrgDetails] = useState<ApiOrganization>();
  const [hasOrgDetailsError, setHasOrgDetailsError] = useState<boolean>(false);
  // const [hasProfileError, setHasProfileError] = useState<boolean>(false);
  const [isHeaderButtonLoading, setIsHeaderButtonLoading] = useState<boolean>(false);
  const [isHeaderButtonDisabled, setIsHeaderButtonDisabled] = useState<boolean>(true);
  const [showOrgDetailsError, setShowOrgDetailsError] = useState<boolean>(false);
  // const [showProfileError, setShowProfileError] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [isToastOpen, setIsToastOpen] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'neutral'>('success');
  const [profileIndex, setProfileIndex] = useState<number>(0);
  // const [saveAndRefresh, setSaveAndRefresh] = useState<boolean>(false);
  const [,setValueForSaveBtn] = useState<number>(0);
  const [openDirtyModal, setOpenDirtyModal] = useState<boolean>(false);
  const [validationList, setValidationList] = useState<{[key: string]: string[]}>({});
  const [originalOrgDetails, setOriginalOrgDetails] = useState<ApiOrganization>();
  const [imageURL, setImageURL] = useState<string | undefined>('');
  const [originalImageURL, setOriginalImageURL] = useState<string | undefined>('');
  const [parentOrg, setParentOrg] = useState<ApiOrganization | null>(null);
  // const [errorCaptionList, setErrorCaptionList] = useState<string[]>([]);
  const [isOrgDetailsDirty, setIsOrgDetailsDirty] = useState<boolean>(false);
  const [summaryData, setSummaryData] = useState<ApiOrgOverview>({});
  const [licenseFlags, setLicenseFlags] = useState<IlicenseFlags | undefined>();
  const [deactivateModal, setDeactivateModal] = useState<boolean>(false);
  const [statusButtonLoading, setStatusButtonLoading] = useState<boolean>(false);
  const [enumList, setEnumList] = useState<ApiEnumGroup[]>([]);
  const [tagList, setTagList] = useState<ApiCustomField[]>(hasTagList);

  const isInactive = orgDetails?.activeStatus === TEMPLATE_STATUS.Not_Active.id;
  const orgType = functionStructureList?.find((item) => {
    return item.id === orgDetails?.function;
  })?.name || '';

  const statusContentIds = {
    title: isInactive ? 'REACTIVATE_ORGANIZATION_TITLE' : 'DEACTIVATE_ORGANIZATION_TITLE',
    buttonLabel: isInactive ? 'REACTIVATE' : 'DEACTIVATE',
    confirmButtonLabel: isInactive ? 'REACTIVATE_CONFIRM' : 'DEACTIVATE_MEMBERS_BTN',
    successMsg: isInactive ? 'REACTIVATE_SUCCESS' : 'DEACTIVATE_SUCCESS',
  };
  
  const handleOrgChange = (data: ApiOrganization) => {
    setOrgDetails(data);
    showOrgDetailsError && validateForm(data);
  };

  const handleSaveOrgInfoChange = async () => {
    const isFormValid = validateForm();
    
    if (!isFormValid) {
      setShowOrgDetailsError(true);
      setToastSeverity('error');
      setIsToastOpen(true);
      setToastMessage(messages({ id: 'MEMBER_MISSING_FIELD_TOAST' }));
      return;
    }
    
    setIsHeaderButtonLoading(true);

    /** Save image */
    if (!isEqual(imageURL, originalImageURL)) {
      try {
        const payload = {
          itemId: orgDetails?.id,
          typeId: ORG_TYPE_ID,
          base64: imageURL,
        };

        const response = await dispatch(saveImage(payload as ApiImageWrapper));

        if (response.status === SUCCESS_STATUS) {
          setToastSeverity('success');
          setToastMessage(messages({ id: 'ORG_SAVE_SUCCESS' }));
          setOriginalImageURL(imageURL);
          setIsToastOpen(true);
        }
      } catch (err) {
      // eslint-disable-next-line no-console
        console.error(err);
        setIsHeaderButtonLoading(false);
        setToastSeverity('error');
        setToastMessage(messages({ id: 'ERROR_MESSAGE' }));
        setIsToastOpen(true);
        return;
      }
    }
    if (orgDetails && !isEqual(orgDetails, originalOrgDetails)) {
      if (!isFormValid) {
        setShowOrgDetailsError(true);
        setToastSeverity('error');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'MEMBER_EDIT_SAVE_ERROR_MESSAGE' }));
        return;
      }
      try {
        await dispatch(saveOrg(Number(orgId), orgDetails as ApiOrganization));
        setToastSeverity('success');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'ORG_SAVE_SUCCESS' }));
        setHasOrgDetailsError(false);
        dispatch(setIsDirty_Org_Details(false));
        fetch(Number(orgId));
      } catch (e) {
        setToastSeverity('error');
        setIsToastOpen(true);
        setToastMessage(messages({ id: 'ORG_SAVE_FAIL' }));
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setIsHeaderButtonLoading(false);
      }
    }
    else {
      setIsHeaderButtonLoading(false);
    }
  };

  const validateForm = (data: ApiOrganization | undefined = orgDetails) => {
    let isValid = true;

    /** name validation */
    const name: string[] = [];
    if (!data?.name || data?.name.trim() === '') {
      isValid = false;
      name.push(messages({ id: 'ORGANIZATION_NAME_EMPTY_CAPTION' }));
    }
    if (data?.name && data?.name?.length > STANDARD_TEXT_MAX_LENGTH) {
      isValid = false;
      name.push(messages({ id: 'ORGANIZATION_NAME_MAX_50' }));
    }

    /** notes validation */
    const notes: string[] = [];
    const hasNotes = data?.data?.find((item) =>
      item.key.toUpperCase() ===
        MEMBER_FIELD_NAME_NOTES.toUpperCase()
    );
    if (hasNotes && hasNotes?.value && hasNotes?.value?.length > ORG_NOTES_MAX_LENGTH) {
      isValid = false;
      notes.push(messages({ id: 'MEMBER_PERSONAL_DETAILS_NOTES_PLACEHOLDER' }));
    }

    /** countryId validation */
    const countryId: string[] = [];
    if (!data?.address?.countryId) {
      isValid = false;
      countryId.push(messages({ id: 'MEMBER_COUNTRY_CAPTION' }));
    }

    /** province validation */
    const province: string[] = [];
    if (data?.address?.province && data?.address?.province?.length > STANDARD_TEXT_MAX_LENGTH) {
      isValid = false;
      province.push(messages({ id: 'MEMBER_PROVINCE_MAX_50_CAPTION' }));
    }

    /** province validation */
    const city: string[] = [];
    if (data?.address?.city && data?.address?.city?.length > STANDARD_TEXT_MAX_LENGTH) {
      isValid = false;
      city.push(messages({ id: 'MEMBER_CITY_MAX_50_CAPTION' }));
    }

    /** province validation */
    const postCode: string[] = [];
    if (data?.address?.postCode && data?.address?.postCode?.length > POST_CODE_MAX_LENGTH) {
      isValid = false;
      postCode.push(messages({ id: 'MEMBER_POSTCODE_MAX_20_CAPTION' }));
    }

    /** addressLine fields validation */
    const addressLineFields = ['addressLine1', 'addressLine2', 'addressLine3'];
    const addresslines: {[key: string]: string[]} = {
      addressLine1: [],
      addressLine2: [],
      addressLine3: [],
    };
    for (const field of addressLineFields) {
      if (data?.address && data?.address?.[field as keyof ApiAddress] 
        && String(data?.address?.[field as keyof ApiAddress])?.length > ADDRESS_MAX_LENGTH
      ) {
        isValid = false;
        addresslines[field].push(messages({ id: 'MEMBER_ADDRESS_MAX_20_CAPTION' }));
      }
    }

    setValidationList((prevState) => ({
      ...prevState,
      name,
      notes,
      countryId,
      province,
      city,
      postCode,
      addressLine1: addresslines.addressLine1,
      addressLine2: addresslines.addressLine2,
      addressLine3: addresslines.addressLine3,
    }));
    
    setHasOrgDetailsError(!isValid);
    
    // 
    return isValid;
  };

  const fetchTagList = useCallback(async (orgData: ApiOrganization) => {
    if (tagList?.length || !orgData?.function) return;
    try {
      const data = { itemType: [CustomFieldItemType.Organization], functionId: orgData?.function };
      const fetch = await dispatch(getCustomFieldByTagAndFunction(data));
      if (fetch.data) {
        return fetch?.data;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [dispatch, tagList]);

  // Put all tagList that are not listed in orgData; doing this will include them on save.
  const includeTagList = (orgData: ApiOrganization, tagList: ApiCustomField[]) => {
    if (tagList && tagList?.length && orgData) {
      const childTagList = tagList.filter(({ fieldType }) => fieldType !== MEMBER_PARENT_FIELD_TYPE_NUMBER);
      
      const customField = cloneDeep(orgData?.data) || [];
      const customFieldKeys = customField.map(({ key }) => key);
      const includeRemainingList = childTagList
        .filter(({ key }) => !customFieldKeys.includes(key || ''))
        .map(({ key, id, itemType }) => ({
          key: key || '',
          value: '',
          id,
          itemType,
        }));
      return {
        ...orgData,
        data: [...customField, ...includeRemainingList],
      };
    }
  };

  const fetchCustomDropdownOptions = useCallback(async () => {
    if (hasEnumList?.length) return;
    try {
      const orgEnum = await dispatch(getEnumList());
      if (orgEnum.data) {
        setEnumList(orgEnum.data);
        dispatch(setOrg_EnumList(orgEnum.data));
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [dispatch, hasEnumList]);
  
  const fetch = useCallback(async (orgId: number) => {
    try {
      const response = await dispatch(getOrganization(orgId));
      if (response?.data) {
        setOriginalOrgDetails(response.data);
        const fetchedTagList = await fetchTagList(response.data);
        if (fetchedTagList?.length) {
          const updatedOrgData = includeTagList(response?.data || [], fetchedTagList || []);
          setTagList(fetchedTagList);
          dispatch(setOrg_TagList(fetchedTagList));
          setOrgDetails(updatedOrgData ?? response?.data);
        }
        else {
          setOrgDetails(response?.data);
        }
        dispatch(setSelectedOrgName(response?.data?.name || ''));

        /** Set total profiles data */
        const data = await dispatch(getOrgSummary(Number(response?.data?.id)));
        setSummaryData(data?.data);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
    
    try {
      const licenseResponse = await dispatch(getLicenseInfo());
      setLicenseFlags(licenseResponse.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }, [dispatch, fetchTagList]);

  const getProfileImage = async (imageId: number) => {
    try {
      const response = await dispatch(getImage(ORG_TYPE_ID, imageId));
      const imgUrl = response?.data;
      /** API might return an empty string */
      if (imgUrl?.length > 0) {
        setOriginalImageURL(imgUrl?.includes('data:image') ? imgUrl : 'data:image/jpeg;base64,' + imgUrl);
        setImageURL(imgUrl?.includes('data:image') ? imgUrl : 'data:image/jpeg;base64,' + imgUrl);
      }
      else {
        setOriginalImageURL('');
        setImageURL('');
      }
    } catch (error) {
      setImageURL('');
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const onImageUpdate = (base64?: string) => {
    setImageURL(base64 ?? '');
  };

  const resetOrgDetails = () => {
    setOrgDetails(originalOrgDetails);
  };

  const changeOrgStatus = async () => {
    setStatusButtonLoading(true);
    try {
      const response = await dispatch(changeStatus(ORG_TYPE_ID, Number(orgDetails?.id), isInactive ? TEMPLATE_STATUS.Active.id :  TEMPLATE_STATUS.Not_Active.id));
      if (response.status === SUCCESS_STATUS) {
        fetch(Number(orgId));
        setToastSeverity('success');
        setToastMessage(messages({ id: statusContentIds?.successMsg }));
        setOriginalImageURL(imageURL);
        setIsToastOpen(true);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setStatusButtonLoading(false);
      setDeactivateModal(false);
    }
  };
  
  useEffect(() => {
    fetch(Number(orgId));
    fetchCustomDropdownOptions();
  }, [orgId, fetch, fetchCustomDropdownOptions]);

  useEffect(() => {
    const fetchParentOrg = async () => {
      if (!orgDetails?.relationships || !orgDetails?.relationships[0]?.orgBId) {
        setParentOrg(null); // Handle the case where parentOrgId is undefined
        return;
      }
      
      const parentOrgId = Number(orgDetails.relationships[0].orgBId);
      const result = await dispatch(getOrganization(parentOrgId));
      setParentOrg(result?.data);
    };
    
    if (!isRootOrg) {
      fetchParentOrg();
    }
  }, [orgDetails?.relationships, dispatch, isRootOrg]);

  /** Check if org has unsaved changes */
  useEffect(()=>{
    const isOrgDetailsDirtyTemp = !isEqual(orgDetails, originalOrgDetails) || !isEqual(imageURL, originalImageURL);
    setIsOrgDetailsDirty(isOrgDetailsDirtyTemp);
    if (isOrgDetailsDirtyTemp || isOrgDetailsDirtyTemp){
      dispatch(setIsDirty_Org_Details(true));
    } else {
      dispatch(setIsDirty_Org_Details(false));
    }
  },[orgDetails, originalOrgDetails, dispatch, isOrgDetailsDirty, setIsOrgDetailsDirty, imageURL, originalImageURL]);

  /** Handle save button disable toggle */
  useEffect(()=>{
    const isHeaderBtnDisabled = !editAccess || !isOrgDetailsDirty;
    setIsHeaderButtonDisabled(isHeaderBtnDisabled);
  },[editAccess, isOrgDetailsDirty]);

  /** Get existing image */
  useEffect(() => {
    if (!orgDetails) return;
    if (orgDetails?.imageId !== 0 && imageURL === '' && orgDetails?.imageId !== undefined) {
      getProfileImage(Number(orgDetails?.imageId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgDetails]);

  useEffect(() => {
    if (functionStructureList?.length) return;
    const fetch = async () => {
      // if there is no structure list, fetch the list again
      try {
        const structure = await dispatch(getStructure());
        if (structure?.data.length) {
          dispatch(setFunctionStructureList(structure.data));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    };
    fetch();
  }, [dispatch, functionStructureList?.length]);

  const tabsData: AgTabsDataProps[][] = [ // if there are multiple profiles, the sequence of data and tabs data should be the same
    [
      {
        hash: '#organization',
        label: messages({ id: 'ORGANIZATION_DETAILS' }),
        children: <OrganizationDetails
          setOrgDetails={handleOrgChange}
          identity={orgDetails}
          editAccess={editAccess}
          showError={showOrgDetailsError}
          validationList={validationList}
          imageURL={imageURL}
          onImageUpdate={onImageUpdate}          
          tagList={tagList}
          enumList={enumList}
        />
      },
      {
        hash: '#settings',
        label: messages({ id: 'Settings' }),
        children: <Settings
          parentOrg={parentOrg as ApiOrganization}
          editAccess={editAccess}
          handleActiveStatus={() => setDeactivateModal(true)}
          activateBtnLabel={messages({ id: statusContentIds?.buttonLabel || '' })}
          deforestActive={licenseFlags?.canDoDeforestCheck}
          orgType={orgType}
        />
      }
    ].concat((licenseFlags?.canDoContracts) ? {
      hash: '#contracts',
      label: messages({ id: 'CONTRACTS' }),
      children: (
        <ContractForm
          canEdit={editAccess}
          itemRef={[{ itemId: orgId, itemType: ApiItemTypes.Organization }]} 
        />
      )
    } : [])
    .concat((canDoOther) ? {
      hash: '#other',
      label: messages({ id: 'Other' }),
      children: <Other />
    } : [])
  ];

  const viewAvatarModal = (
    <AgModal
      closeIcon
      open={isOpenViewAvatar}
      setOpen={() => {
        dispatch(setIsOpenViewAvatar(false));
      }}
      title={messages({ id: 'MEMBER_AVATAR_POPUP_TITLE' })}
      body={
        <AgTypography>
          <AgTypography
            contentStyle={'AP.Paragraph.Large.Regular'}
            style={{ marginTop: '16px' }}
            message={orgDetails?.name || ''}
          />
          <AgTypography style={{ marginTop: '15px', textAlign: 'center' }}>
            {imageURL ?
              <img
                src={imageURL}
                alt={messages({ id: 'MEMBER_PERSONAL_DETAILS_PHOTO' })}
                style={{ width: '508px', height: '466px', objectFit: 'contain' }}
              /> :
              <AgAvatar
                src={defaultAvatar} alt={messages({ id: 'AVATAR_ALT' })}
                style={{ width: '508px', height: '466px' }}
              />}
          </AgTypography>
        </AgTypography>
      }
    />
  );

  const profileUnsavedModal = ( // because this modal would redirect in-between tabs, instead of navigating to other pages, we need it separately
    openDirtyModal &&
    <AgModal
      closeIcon
      open={openDirtyModal}
      setOpen={() => {
        dispatch(setIsOpenUnsavedModal(false));
      }}
      title={messages({ id: 'MEMBER_UNSAVED_MODAL_TITLE' })}
      body={
        <AgTypography
          contentStyle={'AP.Paragraph.Large.Regular'}
          style={{ marginTop: '16px', marginBottom: '80px' }}
          message={messages({ id: 'MEMBER_UNSAVED_MODAL_TEXT' })}
        />
      }
      footer={
        <>
          <AgButton
            label={messages({ id: 'CANCEL' })}
            variant='outlined'
            style={{ marginRight: '32px' }}
            onClick={() => {
              setOpenDirtyModal && setOpenDirtyModal(false);
            }}
          />
          <AgButton
            label={messages({ id: 'PROCEED' })}
            onClick={() => {
              dispatch(setIsDirty_Org_Details(false));
              setShowOrgDetailsError(false);
              setOpenDirtyModal && setOpenDirtyModal(false);
              resetOrgDetails && resetOrgDetails();
              setSelectedCard && setSelectedCard(profileIndex ?? 0);
            }}
          />
        </>
      }
      footerSX={{
        boxSizing: 'border-box'
      }}
    />
  );

  return (
    <AgTypography
      style={{
        display: 'flex',
        gap: '8px',
        justifyContent: 'start',
        // padding: '16px 32px',
        minHeight: '100%'
      }}
    >
      <AgTypography
        style={{
          flex: 1,
          maxWidth: '880px'
        }}
      >
        <AgProfilePage
          headerHasPersonalDetailsError={hasOrgDetailsError && showOrgDetailsError}
          headerTabsData={tabsData || []}
          headerButtonLabel={messages({ id: 'SAVE' })}
          headerButtonDisabled={isHeaderButtonDisabled}
          headerCardTitle={messages({ id: 'CERTIFICATION_ORG_ID' })}
          headerCardName={orgDetails?.id ? '#c' + orgDetails?.id?.toString() : ''}
          headerButtonOnClick={handleSaveOrgInfoChange}
          isHeaderButtonLoading={isHeaderButtonLoading}
          navButtonOnClick={() => {
          }}
          navHideInfoCard={true}
          navAccountImg={imageURL || defaultAvatar}
          navAccountUserType={orgDetails?.purpose}
          navAccountName={orgDetails?.name || ''}
          navAccountID={orgDetails?.id ? '#c' + orgDetails?.id?.toString() : ''}
          navAccountVillage={orgDetails?.address?.city || ''}
          navAccountEmail={orgDetails?.salesEmail ?? ''}
          navAccountPhone={orgDetails?.phoneNumber ?? ''}
          navInactive={isInactive}
          navMemberProfile={orgDetails?.activeStatus}
          resetPersonalDetailsToOriginal={resetOrgDetails}
          selectedCard={selectedCard}
          setSelectedCard={setSelectedCard}
          isDirty={isOrgDetailsDirty}
          openDirtyModal={openDirtyModal}
          setOpenDirtyModal={setOpenDirtyModal}
          profileIndex={profileIndex}
          setProfileIndex={setProfileIndex}
          profileUnsavedModal={profileUnsavedModal}
          viewAvatarModal={viewAvatarModal}
          style={{
            height: '100%'
          }}
          navHideAddButton
          setValueForSaveBtn={setValueForSaveBtn}
          navigationHeaderTitle1={messages({ id: 'ORGANIZATION_PAGE_NAVI_HEADER' })}
          accountProfileCardTitle={messages({ id: 'ORGANIZATION_DETAILS' })}
          accountProfileCardIdLabel={messages({ id: 'CERTIFICATION_ORG_ID' })}
        />
      </AgTypography>
      <RightSidebar orgData={orgDetails} summaryData={summaryData} />

      {/* Unsaved changes modal */}
      <AgModal
        closeIcon
        open={isOpenUnsavedModal}
        setOpen={() => {
          dispatch(setIsOpenUnsavedModal(false));
        }}
        title={messages({ id: 'MEMBER_UNSAVED_MODAL_TITLE' })}
        body={
          <AgTypography
            contentStyle={'AP.Paragraph.Large.Regular'}
            style={{ marginTop: '16px', marginBottom: '80px' }}
            message={messages({ id: 'MEMBER_UNSAVED_MODAL_TEXT' })}
          />
        }
        footer={
          <>
            <AgButton
              label={messages({ id: 'CANCEL' })}
              variant='outlined'
              style={{ marginRight: '32px' }}
              onClick={() => {
                dispatch(setIsOpenUnsavedModal(false));
              }}
            />
            <AgButton
              label={messages({ id: 'PROCEED' })}
              onClick={() => {
                dispatch(setIsOpenUnsavedModal(false));
                setShowOrgDetailsError(false);
                dispatch(setIsDirty_Org_Details(false));
                navigate(redirect_URL, { replace: true });
                dispatch(setOrg_Redirect_URL(''));
              }}
            />
          </>
        }
        footerSX={{
          boxSizing: 'border-box'
        }}
      />

      {/* Deactivate org modal */}
      <AgModal
        open={deactivateModal}
        setOpen={setDeactivateModal}
        closeIcon
        state={messages({ id: 'WARNING' })}
        title={messages({ id: statusContentIds?.title ?? '' })}
        body={
          <>
            <AgTypography
              style={{ marginTop: '16px', marginBottom: '128px' }}
              contentStyle={'AP.Paragraph.Large.Regular'}
              component='div'
            >
              <FormattedMessage id='DEACTIVATE_ORGANIZATION_MSG' />
            </AgTypography>
          </>
        }
        footer={
          <>
            <AgButton
              label={messages({ id: 'CANCEL' })}
              variant='outlined'
              style={{ marginRight: '32px' }}
              onClick={() => setDeactivateModal(false)}
            />
            <AgButton
              disabled={statusButtonLoading}
              startIcon={statusButtonLoading ? <CircularProgress color='inherit' size={13} /> : null}
              label={messages({ id: statusContentIds?.confirmButtonLabel ?? '' })}
              onClick={changeOrgStatus}
            />
          </>
        }
      />

      {isToastOpen && <AgToast open={isToastOpen} temporary onIconClick={() => {
        setIsToastOpen(false);
      }} severity={toastSeverity} message={toastMessage} />}

    </AgTypography>
  );
};
 
export default OrganizationInfo;